import { formatCurrency } from '../utils/format';
import {
  getOrderFinancialBreakdown,
  merchandiseFromOrderItems,
  ORDER_TAX_PERCENT,
  type OrderChargeFields,
} from '../utils/orderCheckoutCharges';

type ChargeItem = {
  price?: number | null;
  quantity?: number | null;
};

export default function OrderChargesCard({
  order,
  items,
}: {
  order: OrderChargeFields;
  items?: ChargeItem[] | null;
}) {
  const itemMerch = merchandiseFromOrderItems(items);
  const fin = getOrderFinancialBreakdown({
    ...order,
    merchandise_subtotal_jod:
      order.merchandise_subtotal_jod != null &&
      Number.isFinite(Number(order.merchandise_subtotal_jod))
        ? order.merchandise_subtotal_jod
        : itemMerch > 0
          ? itemMerch
          : order.merchandise_subtotal_jod,
  });

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">
        Charges
      </h3>
      <div className="space-y-1.5 text-sm">
        <div className="flex justify-between">
          <span>Subtotal:</span>
          <span>{formatCurrency(fin.subtotal)}</span>
        </div>
        {fin.promotionDiscount > 0 && (
          <div className="flex justify-between text-emerald-800 font-medium">
            <span>Promotion discount:</span>
            <span>-{formatCurrency(fin.promotionDiscount)}</span>
          </div>
        )}
        {fin.dineInDiscount > 0 && (
          <div className="flex justify-between text-emerald-800 font-medium">
            <span>
              Dine-in discount
              {order.dine_in_discount_percent ? ` (${order.dine_in_discount_percent}%)` : ''}:
            </span>
            <span>-{formatCurrency(fin.dineInDiscount)}</span>
          </div>
        )}
        {fin.pointsDiscount > 0 && (
          <div className="flex justify-between text-amber-800">
            <span>Points discount:</span>
            <span>-{formatCurrency(fin.pointsDiscount)}</span>
          </div>
        )}
        {fin.walletUsed > 0 && (
          <div className="flex justify-between text-amber-800">
            <span>Wallet credit:</span>
            <span>-{formatCurrency(fin.walletUsed)}</span>
          </div>
        )}
        <div className="flex justify-between text-gray-700">
          <span>Delivery fee:</span>
          <span>{formatCurrency(fin.deliveryFee)}</span>
        </div>
        <div className="flex justify-between text-gray-700">
          <span>Service fee ({fin.serviceFeePercent}%):</span>
          <span>{formatCurrency(fin.serviceFee)}</span>
        </div>
        <div className="flex justify-between text-gray-700">
          <span>Small order fee:</span>
          <span>{formatCurrency(fin.smallOrderFee)}</span>
        </div>
        <div className="flex justify-between text-gray-700">
          <span>Tax (Jordanian tax {ORDER_TAX_PERCENT}%):</span>
          <span>{formatCurrency(fin.tax)}</span>
        </div>
      </div>
    </div>
  );
}

export function orderChargesReceiptLines(order: OrderChargeFields, items?: ChargeItem[] | null): string[] {
  const itemMerch = merchandiseFromOrderItems(items);
  const fin = getOrderFinancialBreakdown({
    ...order,
    merchandise_subtotal_jod:
      order.merchandise_subtotal_jod != null &&
      Number.isFinite(Number(order.merchandise_subtotal_jod))
        ? order.merchandise_subtotal_jod
        : itemMerch > 0
          ? itemMerch
          : order.merchandise_subtotal_jod,
  });
  const lines = [
    `Subtotal: ${formatCurrency(fin.subtotal)}`,
  ];
  if (fin.promotionDiscount > 0) {
    lines.push(`Promotion discount: -${formatCurrency(fin.promotionDiscount)}`);
  }
  if (fin.dineInDiscount > 0) {
    lines.push(`Dine-in discount: -${formatCurrency(fin.dineInDiscount)}`);
  }
  if (fin.pointsDiscount > 0) {
    lines.push(`Points discount: -${formatCurrency(fin.pointsDiscount)}`);
  }
  if (fin.walletUsed > 0) {
    lines.push(`Wallet credit: -${formatCurrency(fin.walletUsed)}`);
  }
  lines.push(
    `Delivery fee: ${formatCurrency(fin.deliveryFee)}`,
    `Service fee (${fin.serviceFeePercent}%): ${formatCurrency(fin.serviceFee)}`,
    `Small order fee: ${formatCurrency(fin.smallOrderFee)}`,
    `Tax (Jordanian tax ${ORDER_TAX_PERCENT}%): ${formatCurrency(fin.tax)}`,
    `Total: ${formatCurrency(fin.netPayout)}`
  );
  return lines;
}
